/**
 * Cross-browser QR decoder. Uses the native BarcodeDetector API where
 * available (Chrome Android, Safari 17+, most modern Chromiums) and falls
 * back to jsqr in JS for everything else (Firefox, older Safari).
 *
 * Both paths read from a `<video>` element by drawing the current frame
 * into a hidden canvas — there's no API to feed the BarcodeDetector a
 * video frame directly across all browsers, and going through canvas
 * keeps the fallback path identical.
 */

type BarcodeDetectorCtor = new (options?: { formats?: ReadonlyArray<'qr_code' | string> }) => {
  detect: (
    source: HTMLCanvasElement | HTMLVideoElement | ImageBitmap,
  ) => Promise<Array<{ rawValue: string; format: string }>>;
};

declare global {
  interface Window {
    BarcodeDetector?: BarcodeDetectorCtor;
  }
}

export interface QrDecoder {
  /** Returns the first QR payload found in the current frame, or null. */
  detect: (video: HTMLVideoElement) => Promise<string | null>;
  /** Releases any retained resources. */
  dispose: () => void;
  /** Which backend is in use — useful for analytics / UX hints. */
  readonly kind: 'native' | 'jsqr';
}

export async function createDecoder(): Promise<QrDecoder> {
  // Native path: BarcodeDetector. We feature-detect both the constructor and
  // qr_code support — some browsers ship the constructor but don't actually
  // implement QR formats.
  if (typeof window.BarcodeDetector === 'function') {
    try {
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) throw new Error('no canvas ctx');
      return {
        kind: 'native',
        async detect(video) {
          if (video.videoWidth === 0 || video.videoHeight === 0) return null;
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          try {
            const results = await detector.detect(canvas);
            return results[0]?.rawValue ?? null;
          } catch {
            return null;
          }
        },
        dispose() {
          /* nothing to release */
        },
      };
    } catch {
      // Fall through to jsqr if the constructor throws (Safari sometimes).
    }
  }

  // Fallback: jsqr in JS. ~20KB gzipped, works in every modern browser.
  const { default: jsQR } = await import('jsqr');
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  return {
    kind: 'jsqr',
    async detect(video) {
      if (video.videoWidth === 0 || video.videoHeight === 0) return null;
      // Downscale to ~640px on the long side: jsqr is O(n*m) on pixels and
      // a 1080p frame is overkill — we still detect from ~50cm away.
      const longSide = Math.max(video.videoWidth, video.videoHeight);
      const scale = longSide > 640 ? 640 / longSide : 1;
      canvas.width = Math.floor(video.videoWidth * scale);
      canvas.height = Math.floor(video.videoHeight * scale);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });
      return result?.data ?? null;
    },
    dispose() {
      /* nothing to release */
    },
  };
}
